import { clientName } from '@clim/client-core';
import { APPOINTMENT_STATUSES, isLateIntervention, isOnIntervention, isoDate, type InterventionRecord, type InterventionStatus } from '@clim/domain';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AppointmentDialog, fmtDay, InterventionCard, ReasonDialog } from '../components/Interventions';
import { MyPickupOrders } from '../components/PickupOrders';
import { Empty, ErrorBox, Field, Modal, Textarea } from '../components/ui';
import { errorMessage } from '../lib/errors';
import { useEngine, useReady } from '../lib/session';

type Ask = { id: string; to: InterventionStatus; category: string; title: string; confirm: string };

/**
 * Mes interventions (CDC §9) : À accepter (accepter / refuser avec motif), « Prendre en charge ma
 * tournée » le jour J, puis Sur place → Terminée (commentaire) ou Impossible (motif). Tout fonctionne
 * hors ligne : les actions partent à la prochaine synchronisation.
 */
export function MyInterventionsPage() {
  const engine = useEngine();
  const { me } = useReady();
  const today = isoDate(new Date());
  const [ask, setAsk] = useState<Ask | null>(null);
  const [finish, setFinish] = useState<string | null>(null);
  const [appointment, setAppointment] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mine = engine
    .list('intervention')
    // principal ou en renfort (intervention à plusieurs)
    .filter((i) => isOnIntervention(i, me.user.id))
    .sort((a, b) => `${a.scheduledDate ?? '9999'}${a.slotStart ?? '99'}`.localeCompare(`${b.scheduledDate ?? '9999'}${b.slotStart ?? '99'}`));
  const toAccept = mine.filter((i) => i.status === 'proposed' && i.technicianId === me.user.id);
  const todayList = mine.filter((i) => i.scheduledDate === today && ['scheduled', 'taken_charge', 'on_site'].includes(i.status));
  const tourReady = todayList.filter((i) => i.status === 'scheduled').length;
  // acceptées sans date (le planning fixe le jour) et planifiées à venir ; une acceptation avec date est planifiée d'office
  const upcoming = mine.filter((i) => (i.status === 'accepted' && (i.scheduledDate ?? '9999') >= today) || (i.status === 'scheduled' && (i.scheduledDate ?? '9999') > today));
  // date passée sans compte rendu : à clôturer (Terminée avec photos, ou Impossible)
  const late = mine.filter((i) => isLateIntervention(i, today));
  const run = (p: Promise<unknown>) => {
    setError(null);
    return p.catch((e) => setError(errorMessage(e)));
  };

  const actions = (i: InterventionRecord) => {
    const d = engine.get('dossier', i.dossierId);
    const c = d ? engine.get('client', d.clientId) : undefined;
    const address = [d?.siteAddressLine1 ?? c?.addressLine1, d?.sitePostalCode ?? c?.postalCode, d?.siteCity ?? c?.city].filter(Boolean).join(' ');
    // en renfort : le technicien principal fait avancer l'intervention
    const lead = i.technicianId === me.user.id;
    return (
      <div className="iv-actions">
        {!lead && <span className="badge tone-info">En renfort de {i.technicianName ?? '—'}</span>}
        {c?.phone && (
          <a className="btn small" href={`tel:${c.phone}`}>
            📞 Appeler
          </a>
        )}
        {address && (
          <a className="btn small" href={`https://maps.google.com/?q=${encodeURIComponent(address)}`} target="_blank" rel="noreferrer">
            🧭 Itinéraire
          </a>
        )}
        {d && (
          <Link className="btn small" to={`/dossiers/${d.id}`}>
            Dossier
          </Link>
        )}
        {lead && i.status === 'proposed' && (
          <>
            <button className="btn small primary" onClick={() => void run(engine.transitionIntervention(i.id, 'accepted'))}>
              Accepter
            </button>
            <button className="btn small danger-outline" onClick={() => setAsk({ id: i.id, to: 'to_assign', category: 'intervention_refusal', title: 'Refuser l’intervention', confirm: 'Refuser' })}>
              Refuser
            </button>
          </>
        )}
        {lead && APPOINTMENT_STATUSES.includes(i.status) && (
          <button className={`btn small${!i.scheduledDate || !i.slotStart ? ' primary' : ''}`} onClick={() => setAppointment(i.id)}>
            📅 {i.scheduledDate && i.slotStart ? 'Modifier le rendez-vous' : 'Fixer le rendez-vous'}
          </button>
        )}
        {lead && i.status === 'scheduled' && i.scheduledDate !== null && i.scheduledDate <= today && (
          <button className="btn small primary" onClick={() => void run(engine.transitionIntervention(i.id, 'taken_charge'))}>
            Prendre en charge
          </button>
        )}
        {lead && i.status === 'taken_charge' && (
          <button className="btn small primary" onClick={() => void run(engine.transitionIntervention(i.id, 'on_site'))}>
            Sur place
          </button>
        )}
        {lead && i.status === 'on_site' && (
          <button className="btn small primary" onClick={() => setFinish(i.id)}>
            Terminée
          </button>
        )}
        {lead && ['taken_charge', 'on_site'].includes(i.status) && (
          <button className="btn small danger-outline" onClick={() => setAsk({ id: i.id, to: 'impossible', category: 'intervention_impossible', title: 'Intervention impossible', confirm: 'Confirmer' })}>
            Impossible
          </button>
        )}
      </div>
    );
  };
  const section = (title: string, list: InterventionRecord[], empty?: string) =>
    list.length || empty ? (
      <div className="card">
        <h2>
          {title} {list.length > 0 && <span className="muted">({list.length})</span>}
        </h2>
        {list.length ? (
          list.map((i) => (
            <InterventionCard key={i.id} i={i}>
              {i.scheduledDate !== today && <div className="muted iv-date">{fmtDay(i.scheduledDate)}</div>}
              {i.notes && <div className="pre iv-notes">{i.notes}</div>}
              {actions(i)}
            </InterventionCard>
          ))
        ) : (
          <Empty>{empty}</Empty>
        )}
      </div>
    ) : null;

  return (
    <div>
      <h1>Mes interventions</h1>
      <ErrorBox message={error} />
      {late.length > 0 && (
        <div className="error-box" role="alert">
          ⚠️ <strong>{late.length} intervention(s) en retard</strong> : la date est passée sans compte rendu. Ajoutez les photos puis
          passez-les « Terminée » (ou « Impossible » avec le motif).
        </div>
      )}
      {section('En retard', late)}
      {section('À accepter', toAccept)}
      <div className="card tour">
        <div className="card-head">
          <h2>Aujourd’hui · {fmtDay(today)}</h2>
          {tourReady > 0 && (
            <button className="btn primary" onClick={() => void run(engine.takeTour(today))}>
              Prendre en charge ma tournée ({tourReady})
            </button>
          )}
        </div>
        {todayList.length ? (
          todayList.map((i) => (
            <InterventionCard key={i.id} i={i}>
              {i.notes && <div className="pre iv-notes">{i.notes}</div>}
              {actions(i)}
            </InterventionCard>
          ))
        ) : (
          <Empty>Aucune intervention aujourd’hui.</Empty>
        )}
      </div>
      {section('À venir', upcoming)}
      <MyPickupOrders />
      {ask && (
        <ReasonDialog
          title={ask.title}
          category={ask.category}
          confirm={ask.confirm}
          onClose={() => setAsk(null)}
          onConfirm={(reasonId, comment) => engine.transitionIntervention(ask.id, ask.to, { reasonId, comment })}
        />
      )}
      {finish && <FinishDialog id={finish} onClose={() => setFinish(null)} />}
      {appointment && <AppointmentDialog id={appointment} onClose={() => setAppointment(null)} />}
    </div>
  );
}

function FinishDialog({ id, onClose }: { id: string; onClose(): void }) {
  const engine = useEngine();
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const i = engine.get('intervention', id);
  const d = i ? engine.get('dossier', i.dossierId) : undefined;
  const c = d ? engine.get('client', d.clientId) : undefined;
  return (
    <Modal title={`Intervention terminée — ${c ? clientName(c) : ''}`} onClose={onClose}>
      <div className="form">
        <Field label="Compte rendu (facultatif)">
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Travaux réalisés, tests, remarques…" />
        </Field>
        <p className="muted">Pensez à ajouter les photos de fin de chantier dans le dossier.</p>
        <ErrorBox message={error} />
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Retour
          </button>
          <button
            className="btn primary"
            onClick={() =>
              engine
                .transitionIntervention(id, 'completed', { comment: comment.trim() || null })
                .then(onClose)
                .catch((e) => setError(errorMessage(e)))
            }
          >
            Terminer l’intervention
          </button>
        </div>
      </div>
    </Modal>
  );
}
